import { useContext, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Grid, Link, ListItemText, Menu, MenuItem, TextField, Typography } from '@mui/material';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { visuallyHidden } from '@mui/utils';
import { iUser, UserContext } from '../contexts/UserContext';
import * as yup from 'yup';
import { useFormik } from "formik";
import { formatDate, formatPhoneNumber, formatSSN } from '../utilities/HelperFunctions';
import ConfirmationDialog from './ConfirmationDialog';
import KeycloakService from '../services/KeycloakService';
import userService from '../services/UserService';
import { iUserDTO } from '../utilities/APIInterfaces';
import CustomTextField from './CustomTextField';
import { enqueueSnackbar } from 'notistack';
import { MfaType } from '../utilities/FormEnums';
import { UserType } from '../contexts/AdminUsersDataContext';

const pageSX = {
  tableHead: {
    fontSize: '0.875rem',
    fontWeight: '500',
    px: 2,
    pt: 0,
    pb: 1.25,
    textTransform: 'uppercase'
  },
  tableCell: {
    px: 2,
    py: 1
  },
  updateLink: {
    color: 'var(--link-color)',
    cursor: 'pointer',
    display: 'inline-block',
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.5,
    textDecoration: 'none'
  }
};

export type ProfileFormProps = {
  user: iUser | null | undefined;
  updateUser: (user:iUserDTO) => void;
  isAdminEdit: boolean;
  selectedUserType: UserType | null;
  isReadOnly: boolean;
  isAdminGroup: boolean;
}
export default function ProfileForm(props: ProfileFormProps) {

  const changeEmailUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/`
  + `realms/${process.env.REACT_APP_KEYCLOAK_REALM}/`
  + `protocol/openid-connect/auth`
  + `?client_id=${process.env.REACT_APP_KEYCLOAK_DASHBOARD_CLIENT}`
  + `&redirect_uri=${encodeURIComponent(window.location.href.split('?')[0])}`
  + `&response_mode=query`
  + `&response_type=code`
  + `&scope=openid`
  + `&kc_action=UPDATE_PROFILE`;

  const changePhoneNumberUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/`
  + `realms/${process.env.REACT_APP_KEYCLOAK_REALM}/`
  + `protocol/openid-connect/auth`
  + `?client_id=${process.env.REACT_APP_KEYCLOAK_DASHBOARD_CLIENT}`
  + `&redirect_uri=${encodeURIComponent(window.location.href.split('?')[0])}`
  + `&response_mode=query`
  + `&response_type=code`
  + `&scope=openid`
  + `&kc_action=mobile_number_config`;

  const actionUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/`
  + `realms/${process.env.REACT_APP_KEYCLOAK_REALM}/`
  + `protocol/openid-connect/auth`
  + `?client_id=${process.env.REACT_APP_KEYCLOAK_DASHBOARD_CLIENT}`
  + `&redirect_uri=${encodeURIComponent(window.location.href.split('?')[0])}`
  + `&response_mode=query`
  + `&response_type=code`
  + `&scope=openid`
  + `&kc_action=`;

  const smsMFACodeName = 'mobile-number';

  const [isSaving, setIsSaving] = useState(false);
  const userContext = useContext(UserContext);
  // Action menu under the "..." button in the "Associated Institutions" table
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isActionMenuOpen = Boolean(anchorEl);

  type PhoneDialogOptionsType = {
    isOpen: boolean;
    isWorking: boolean;
  };
  const [phoneDialogOptions, setPhoneDialogOptions] = useState({} as PhoneDialogOptionsType);

  type InstitutionDialogOptionsType = {
    isOpen: boolean;
    isWorking: boolean;
    institutionId: number;
    studentId: string | null;
  }
  const [institutionDialogOptions, setInstitutionDialogOptions] = useState({} as InstitutionDialogOptionsType);

  type DialogOptions<T = {}> = {
    isOpen: boolean;
    isWorking: boolean;
  } & T;
  type EmailDialogData = { email?: string | null };
  type DobDialogData = { dob?: string | null };
  type SsnDialogData = { ssn?: string | null };

  const [emailDialogOptions, setEmailDialogOptions] = useState<DialogOptions<EmailDialogData>>({} as DialogOptions<EmailDialogData>);
  const [dobDialogOptions, setDobDialogOptions] = useState<DialogOptions<DobDialogData>>({} as DialogOptions<DobDialogData>);
  const [ssnDialogOptions, setSsnDialogOptions] = useState<DialogOptions<SsnDialogData>>({} as DialogOptions<SsnDialogData>);

  const basicInfoFormik = useFormik({
    initialValues: {
      // user is null when this page first loads...the useEffect will populate the initialValues once user is set
      firstName: props.user?.firstName || 'Loading...',
      lastName: props.user?.lastName || ''
    },
    validationSchema: yup.object({
      firstName: yup.string().trim().required('First name is required'),
      lastName: yup.string().trim().required('Last name is required')
    }),
    onSubmit: values => SaveChanges(values)
  });

  const phoneFormik = useFormik({
    initialValues: { phoneDialog: props.user?.phone || ''},
    validationSchema: yup.object({
      phoneDialog: yup.string().required('Mobile number is required').min(10, 'Mobile number must be 10 digits')
    }),
    onSubmit: values => {
      setPhoneDialogOptions({ ...phoneDialogOptions, isWorking: true });
      const userObj = userService.MapToDto({
        ...props.user,
        phone: values.phoneDialog
      });
      userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
        .then((result: iUserDTO) => props.updateUser(result))
        .finally(() => setPhoneDialogOptions({ ...phoneDialogOptions, isOpen: false, isWorking: false }));
    }
  });

  const dobFormik = useFormik({
    initialValues: { dob: props.user?.dob || ''},
    validationSchema: yup.object({
      dob: yup.date().required('Valid date of birth is required').max(new Date(), 'Date of birth must be in the past')
    }),
    onSubmit: values => {
      setDobDialogOptions({ ...dobDialogOptions, isWorking: true });
      const userObj = userService.MapToDto({
        ...props.user,
        dob: values.dob
      });
      userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
        .then((result: iUserDTO) => {
          if (result) {
            props.updateUser(result);
            enqueueSnackbar('User has been updated', { variant: 'success' });
          }
        })
        .finally(() => setDobDialogOptions({ ...dobDialogOptions, isOpen: false, isWorking: false }));

    }
  });

  const ssnFormik = useFormik({
    initialValues: { ssn: props.user?.ssn || ''},
    validationSchema: yup.object({
    ssn: yup
      .string()
      .required('Social Security Number is required')
      .matches(/^[0-9]{9}$/, 'Social Security Number must be exactly 9 digits')
    }),
    onSubmit: values => {
      setSsnDialogOptions({ ...ssnDialogOptions, isWorking: true });
      const userObj = userService.MapToDto({
        ...props.user,
        ssn: values.ssn
      });
      userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
        .then((result: iUserDTO) => {
          if (result) {
            props.updateUser(result);
            enqueueSnackbar('User has been updated', { variant: 'success' });
          }
        })
        .finally(() => setSsnDialogOptions({ ...ssnDialogOptions, isOpen: false, isWorking: false }));
    }
  });

  useEffect(() => {
    if (dobDialogOptions.isOpen) dobFormik.resetForm({ values: { dob: formatDate(props.user?.dob || '', false, true) }, touched: {} });
    if (ssnDialogOptions.isOpen) ssnFormik.resetForm({ values: { ssn: '' }, touched: {} });
  }, [dobDialogOptions.isOpen, ssnDialogOptions.isOpen]);

  useEffect(() => {
    if (props.user) {
      /*
        If Keycloak email doesn't match user.email (e.g database value), update the database.
        Assumption: User changed email address via the "Edit" link and are now "returning" to the app.
      */
      if (!props.isAdminEdit && KeycloakService.getTokenParsed()?.email !== props.user.email) {
        const userObj = userService.MapToDto({
          ...props.user,
          email: KeycloakService.getTokenParsed()?.email
        });
        userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
          .then((result: iUserDTO) => props.updateUser(result));
      }

      basicInfoFormik.setValues({
        firstName: props.user.firstName || '',
        lastName: props.user.lastName || ''
      });
      phoneFormik.setValues({ phoneDialog: props.user.phone || '' });
    }
  }, [props.user]);

  // Associated Institutions changes
  useEffect(() => {
    let userObj = {} as iUserDTO;

    if (institutionDialogOptions.isWorking) {
      if (institutionDialogOptions.studentId) {
        // TRUTHY studentId means we're editing a student's Id...
        userObj = userService.MapToDto({
          ...props.user,
          schools: props.user?.schools?.map(school => {
            // Find the school that has the edited student id and update it
            if (school.id === institutionDialogOptions.institutionId) {
              return {
                ...school,
                school_Student_Id: institutionDialogOptions.studentId
              };
            } else {
              return school;
            }
          })
        });
        userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
          .then((result: iUserDTO) => props.updateUser(result));
      } else {
        // ...else we're removing an institution
        userObj = userService.MapToDto({
          ...props.user,
          schools: props.user?.schools?.filter(school => school.id !== institutionDialogOptions.institutionId) || []
        });
        userService.DeleteStudentSchool(KeycloakService.getId() || null, institutionDialogOptions.institutionId, (v) => v)
          .then(() => props.updateUser(userObj));
      }
      setInstitutionDialogOptions({ ...institutionDialogOptions, isOpen: false, isWorking: false });
    }
  }, [institutionDialogOptions]);

  useEffect(() => {
    if (emailDialogOptions.isWorking) {
      let userObj: iUserDTO = userService.MapToDto({
        ...props.user,
        email: emailDialogOptions.email
      });
      userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
        .then((result: iUserDTO) => {
          if (result) {
            props.updateUser(result);
            enqueueSnackbar('User has been updated',{variant:'success'});
          }
        });
      setEmailDialogOptions({ ...emailDialogOptions, isOpen: false, isWorking: false });
    }
  }, [emailDialogOptions]);

  const SaveChanges = async (formData: any) => {
    setIsSaving(true);

    const userObj = userService.MapToDto({
      ...props.user,
      firstName: formData.firstName,
      lastName: formData.lastName
    });

    userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v)
      .then((result: iUserDTO) => props.updateUser(result))
      .finally(() => setIsSaving(false));
      enqueueSnackbar('User has been updated',{variant:'success'});
  }


  const handleActionButtonClick = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleActionMenuItemClick = (e: React.MouseEvent<HTMLLIElement>) => {
    const id = Number(anchorEl!.getAttribute('data-row-id'));
    const action = (e.target as HTMLElement).closest('li')?.getAttribute('data-menu-action')?.toLowerCase();

    switch (action) {
      case 'edit-id-number':
        const studentId = props.user?.schools?.find(school => school.id === id)?.school_Student_Id || '';
        setInstitutionDialogOptions({ ...institutionDialogOptions, isOpen: true, isWorking: false, institutionId: id, studentId: studentId });
        break;
      case 'remove-institution':
        setInstitutionDialogOptions({ ...institutionDialogOptions, isOpen: true, isWorking: false, institutionId: id, studentId: null });
        break;
    }

    setAnchorEl(null);
  };

  const handleActionMenuClose = (e: React.MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
    setAnchorEl(null);
  };

  const handleEditEmailClick = () => {
    setEmailDialogOptions({ ...emailDialogOptions, isOpen: true, isWorking: false, email: props.user?.email });
  };

  const handleForgetMFA = async () => {
    if (props.user?.mfaOptions){
      let smsCredentialId = props.user?.mfaOptions.find(mfa => mfa.type == smsMFACodeName)?.id;
      if (smsCredentialId){
        await forgetCredential(smsCredentialId);
      }
    }
  };

  const forgetCredential = async (credentialId: string) => {
    await userService.ForgetCredential(props.user?.authId ?? undefined, credentialId, () => { }).then((forceSetup) => {
      if (forceSetup){
        if(!props.isAdminEdit){
          window.location.href = actionUrl+`CONFIGURE_TOTP`;
        }
        else{
          userService.UpdateMfaPreference(props.user?.authId ?? undefined, MfaType.AUTHENTICATOR_APP, () => { }).then(() => {
            userService.GetUser(props.user?.authId ?? undefined, (v) => v)
              .then((result) => {
                props.updateUser(result);
              });
          }); 
        }
      }
      else{
        userService.GetUser(props.user?.authId ?? undefined, (v) => v)
          .then((result) => {
            props.updateUser(result);
          });
      }
    });
  };

  return (<>
    <Container sx={{ p: { xs: 2.25, sm: 4.5 } }}>

      <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 4.5 }}>Basic Information</Typography>

      <Box component='form' noValidate onSubmit={basicInfoFormik.handleSubmit}>
        <Grid container rowGap={4} columnSpacing={3.5} sx={{ mb: 6.25 }}>

          {/* First Name ------------------------------------------------------ */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              autoFocus
              id='firstName'
              labelText='First Name'
              placeholder='Enter your first name'
              disabled={props.isReadOnly}
              shrinkLabel
              value={basicInfoFormik.values.firstName}
              onBlur={basicInfoFormik.handleBlur}
              onChange={basicInfoFormik.handleChange}
              onFocus={e => e.target.select()}
              error={basicInfoFormik.touched.firstName && Boolean(basicInfoFormik.errors.firstName)}
              helperText={basicInfoFormik.touched.firstName && basicInfoFormik.errors.firstName}
            />
          </Grid>

          {/* Last Name ------------------------------------------------------- */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              id='lastName'
              labelText='Last Name'
              placeholder='Enter your last name'
              disabled={props.isReadOnly}
              shrinkLabel
              value={basicInfoFormik.values.lastName}
              onBlur={basicInfoFormik.handleBlur}
              onChange={basicInfoFormik.handleChange}
              onFocus={e => e.target.select()}
              error={basicInfoFormik.touched.lastName && Boolean(basicInfoFormik.errors.lastName)}
              helperText={basicInfoFormik.touched.lastName && basicInfoFormik.errors.lastName}
            />
          </Grid>

          {/* Date of Birth --------------------------------------------------- */}
          {props.selectedUserType !== 'Institution' && <><Grid item xs={12} md={6}>
            <CustomTextField
              id='dob'
              labelText='Date of Birth'
              disabled
              shrinkLabel
              type='date'
              value={formatDate(props.user?.dob || '', false, true)}
            />
            {props.isAdminGroup && <Link
              onClick={() => setDobDialogOptions({ ...dobDialogOptions, isOpen: true, isWorking: false, dob: props.user?.dob })}
              tabIndex={0} // Need this to get it into the tab order since it doesn't have an href
              sx={pageSX.updateLink}
              >Update Date of Birth</Link>}
          </Grid>

          {/* Social Security Number ------------------------------------------ */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              id='last4'
              labelText='Social Security Number'
              disabled
              shrinkLabel
              value={formatSSN(props.user?.last4 || '', true)}
            />
            {props.isAdminGroup && <Link
              onClick={() => setSsnDialogOptions({ ...ssnDialogOptions, isOpen: true, isWorking: false, ssn: props.user?.ssn })}
              tabIndex={0} // Need this to get it into the tab order since it doesn't have an href
              sx={pageSX.updateLink}
              >Update Social Security Number</Link>}
          </Grid></>}

          {/* Email Address --------------------------------------------------- */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              id='email'
              labelText='Email Address'
              disabled
              shrinkLabel
              value={props.user?.email || ''}
            />
            <Link
              href={!props.isAdminEdit ? changeEmailUrl : undefined}
              onClick={e => {
                if(props.isAdminEdit) {
                  e.preventDefault();
                  handleEditEmailClick();
                }
              }}
              tabIndex={0} // Need this to get it into the tab order since it doesn't have an href
              title='Edit email address'
              sx={pageSX.updateLink}
            >{!props.isAdminEdit ? 'Edit' : 'Update Email'}</Link>
          </Grid>

          {/* Mobile Number --------------------------------------------------- */}
          <Grid item xs={12} md={6}>
            <CustomTextField
              id='phone'
              labelText='Mobile Number'
              disabled
              shrinkLabel
              value={formatPhoneNumber(props.user?.phone || '')}
            />
            {!props.isReadOnly && <Link
            href={!props.isAdminEdit ? changePhoneNumberUrl : undefined}
            onClick={async e => {
              if(props.isAdminEdit) {
                e.preventDefault();
                const userObj = userService.MapToDto({
                  ...props.user,
                  phone: '000' //this is just to generate the patch command.
                });
                await userService.UpdateUser(userService.MapToDto(props.user), userObj, (v) => v).then(() =>
                  enqueueSnackbar('Required Action has been created',{variant:'success'})
                );
              }
            }}
              tabIndex={0} // Need this to get it into the tab order since it doesn't have an href
              title='Edit mobile number'
              sx={pageSX.updateLink}
            >{!props.isAdminEdit ? 'Edit' : 'Update Phone Number'}</Link>}
          </Grid>
        </Grid>

        {/* Associated Institutions ------------------------------------------- */}
        {props.selectedUserType !== 'Institution' && <>
        <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 4.5 }}>Associated Institutions</Typography>

        <TableContainer sx={{ mb: 6.25 }}>
          <Table aria-label='associated institutions table'>
            {/* Empty state for associated institutions -----------------------
              Use a caption tag for the empty state to avoid this WAVE alert:
                Alerts - Possible table caption
                What It Means - Text appears to be a table caption, but is not a caption element.
            */}
            {props.user?.schools?.length === 0 && <caption>
                <Typography
                  variant='body1'
                  sx={{
                    color: 'grey.600',
                    fontWeight: 500,
                    py: 3.75,
                    textAlign: 'center'
                  }}
                >No associated institutions found...</Typography>
            </caption>}

            <TableHead><TableRow>
              <TableCell component='th' sx={pageSX.tableHead}>Institution</TableCell>
              <TableCell component='th' sx={pageSX.tableHead}>ID Number</TableCell>
              <TableCell component='th' sx={pageSX.tableHead}>
                {/* Fix for empty header cell accessibility error */}
                <Box sx={visuallyHidden}>Actions</Box>
              </TableCell>
            </TableRow></TableHead>
            <TableBody>
              {props.user?.schools?.map((school, index) => (
                <TableRow key={index}>
                  <TableCell sx={pageSX.tableCell}>{school.school_Name}</TableCell>
                  <TableCell sx={pageSX.tableCell}>{school.school_Student_Id}</TableCell>
                  <TableCell sx={{...pageSX.tableCell, pr: 0, textAlign: 'right' }}>
                    <Button
                      id={`actions-button-${school.id}`}
                      aria-controls={isActionMenuOpen ? 'more-menu' : undefined}
                      aria-expanded={isActionMenuOpen ? 'true' : undefined}
                      aria-haspopup='true'
                      aria-label='Associated institution actions options'
                      color='inherit'
                      data-row-id={school.id}
                      onClick={handleActionButtonClick}
                      onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        // Keyboard accessibility - click Action button when pressing enter or space
                        if (e.key === 'Enter' || e.key === ' ') handleActionButtonClick(e);
                      }}
                      sx={{ minWidth: 'revert' }}
                    >
                      <MoreHorizIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>}

        {/* Don't want the menu to be duplicated for each row in the table */}
        <Menu
          id='actions-menu'
          anchorEl={anchorEl}
          open={isActionMenuOpen}
          onClose={handleActionMenuClose}
          MenuListProps={{
            'aria-labelledby': 'actions-button',
            dense: true,
            sx: {
              minWidth: '180px',
              textAlign: 'right'
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <MenuItem data-menu-action='edit-id-number' onClick={handleActionMenuItemClick}>
            <ListItemText primaryTypographyProps={{ fontWeight: '500' }}>Edit ID Number</ListItemText>
          </MenuItem>
          <MenuItem data-menu-action='remove-institution' onClick={handleActionMenuItemClick}>
            <ListItemText primaryTypographyProps={{ fontWeight: '500' }}>Remove Institution</ListItemText>
          </MenuItem>
        </Menu>

        <Button
          disabled={isSaving || props.isReadOnly}
          variant='contained'
          size='large'
          type='submit'
          sx={{
            fontSize: '1rem',
            px: 5,
            textTransform: 'none',
            bgcolor: 'InceptiaGreen.main',
            '&:hover': {
              backgroundColor: 'InceptiaGreen.dark'
            }
          }}
        >
        {isSaving ? (<>
          <CircularProgress
            color='inherit'
            size={20}
            sx={{ mr: 1 }}
          />
          Saving...
        </>) : 'Save Changes'}</Button>
      </Box>

    </Container>

    {/* Remove institution confirmation dialog -------------------------------- */}
    {institutionDialogOptions.isOpen && institutionDialogOptions.studentId === null && <ConfirmationDialog
      ariaPrefix='remove-form'
      dialogTitle='Remove Institution?'
      singleButton={false}
      ctaButtonText='Remove'
      ctaButtonWorkingText='Removing...'
      cancelButtonText='Cancel'
      open={institutionDialogOptions.isOpen}
      setOpenDialog={() => setInstitutionDialogOptions({ ...institutionDialogOptions, isOpen: false })}
      isWorking={institutionDialogOptions.isWorking}
      setIsWorking={() => setInstitutionDialogOptions({ ...institutionDialogOptions, isWorking: true })}
      width={528}
    >
      <Typography variant='body2'>This will hide any forms and verifications from the institution.</Typography>
    </ConfirmationDialog>}

    {/* Edit student Id number dialog ----------------------------------------- */}
    {institutionDialogOptions.isOpen && institutionDialogOptions.studentId !== null && <ConfirmationDialog
      ariaPrefix='edit-id-form'
      dialogTitle='Edit ID Number'
      singleButton={false}
      ctaButtonText='Save'
      ctaButtonWorkingText='Saving...'
      cancelButtonText='Cancel'
      open={institutionDialogOptions.isOpen}
      setOpenDialog={() => setInstitutionDialogOptions({ ...institutionDialogOptions, isOpen: false })}
      isWorking={institutionDialogOptions.isWorking}
      setIsWorking={() => setInstitutionDialogOptions({ ...institutionDialogOptions, isWorking: true })}
      width={528}
    >
      {/* Input field needs a label, but we don't want to see it */}
      <Typography component='label' htmlFor='student-id' sx={visuallyHidden}>Student Id</Typography>
      <TextField
        autoFocus
        id='student-id'
        placeholder='Enter student ID number'
        fullWidth
        inputProps={{
          // !!! student_school.SCHOOL_STUDENT_ID = varchar(10)
          maxLength: 10,
          // Want the input to have a height of 45px to match Figma
          sx: { py: '11px' }
        }}
        size='small'
        value={institutionDialogOptions.studentId ?? ''}
        onChange={e => setInstitutionDialogOptions({ ...institutionDialogOptions, studentId: e.target.value })}
        onFocus={e => e.target.select()}

        // Need 40px between the input and the buttons to match Figma - dialog by default is giving us 44px in padding - so -4px bottom margin gets us there
        sx={{ mt: 4.5, mb: -0.5 }}
      />
    </ConfirmationDialog>}

    {/* Admin Update Email dialog --------------------------------------------- */}
    {emailDialogOptions.isOpen && <ConfirmationDialog
      ariaPrefix='update-email-form'
      dialogTitle='Update Email'
      singleButton={false}
      ctaButtonText='Update'
      ctaButtonWorkingText='Updating...'
      cancelButtonText='Cancel'
      open={emailDialogOptions.isOpen}
      setOpenDialog={() => setEmailDialogOptions({ ...emailDialogOptions, isOpen: false })}
      isWorking={emailDialogOptions.isWorking}
      setIsWorking={() => setEmailDialogOptions({ ...emailDialogOptions, isWorking: true })}
      width={528}
      dialogSX={{ content: { pb: 0 } }}
    >
      <CustomTextField
        autoFocus
        id='email-id'
        labelText='Email Address'
        placeholder='Enter email'
        shrinkLabel
        sx={{ mt: 2 }}
        type='email'
        value={emailDialogOptions.email ?? ''}
        onChange={e => setEmailDialogOptions({ ...emailDialogOptions, email: e.target.value })}
        onFocus={e => e.target.select()}
      />
    </ConfirmationDialog>}

    {/* Admin Update DOB dialog ----------------------------------------------- */}
    {dobDialogOptions.isOpen && <ConfirmationDialog
      ariaPrefix='update-dob-form'
      dialogTitle='Update Date of Birth'
      singleButton={false}
      ctaButtonText='Update'
      ctaButtonWorkingText='Updating...'
      cancelButtonText='Cancel'
      open={dobDialogOptions.isOpen}
      setOpenDialog={() => setDobDialogOptions({ ...dobDialogOptions, isOpen: false })}
      isWorking={dobDialogOptions.isWorking}
      setIsWorking={() => {
        if (dobFormik.isValid && dobFormik.values.dob) setDobDialogOptions({ ...dobDialogOptions, isWorking: true });
      }}
      width={528}
      dialogSX={{ content: { pb: 0 } }}
      onCtaClick={dobFormik.handleSubmit}
    >
      <CustomTextField
        autoFocus
        id='dob'
        labelText='Date of Birth'
        shrinkLabel
        sx={{ mt: 2 }}
        type='date'
        value={dobFormik.values.dob}
        onBlur={dobFormik.handleBlur}
        onChange={dobFormik.handleChange}
        onFocus={e => e.target.select()}
        error={dobFormik.touched.dob && Boolean(dobFormik.errors.dob)}
        helperText={dobFormik.touched.dob && dobFormik.errors.dob}
      />
    </ConfirmationDialog>}

    {/* Admin Update SSN dialog ----------------------------------------------- */}
    {ssnDialogOptions.isOpen && <ConfirmationDialog
      ariaPrefix='update-ssn-form'
      dialogTitle='Update Social Security Number'
      singleButton={false}
      ctaButtonText='Update'
      ctaButtonWorkingText='Updating...'
      cancelButtonText='Cancel'
      open={ssnDialogOptions.isOpen}
      setOpenDialog={() => setSsnDialogOptions({ ...ssnDialogOptions, isOpen: false })}
      isWorking={ssnDialogOptions.isWorking}
      setIsWorking={() => {
        if (ssnFormik.isValid && ssnFormik.values.ssn) setSsnDialogOptions({ ...ssnDialogOptions, isWorking: true });
      }}
      width={528}
      dialogSX={{ content: { pb: 0 } }}
      onCtaClick={ssnFormik.handleSubmit}
    >
      <CustomTextField
        autoComplete='off'
        autoFocus
        id='ssn'
        labelText='Social Security Number'
        shrinkLabel
        sx={{ mt: 2 }}
        type='text'
        value={ssnFormik.values.ssn}
        onBlur={ssnFormik.handleBlur}
        onChange={e => {
          // Only allow numbers
          const val = e.target.value.replace(/[^0-9]/g, '');
          ssnFormik.setFieldValue('ssn', val);
        }}
        onFocus={e => e.target.select()}
        error={ssnFormik.touched.ssn && Boolean(ssnFormik.errors.ssn)}
        helperText={ssnFormik.touched.ssn && ssnFormik.errors.ssn}
      />
    </ConfirmationDialog>}

  </>);
}